import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { BravosWordmark } from '../components/BravosWordmark';
import { useHideAppNavbar } from '../contexts/AppNavbarVisibilityContext';
import { usePageStatusBar } from '../hooks/usePageStatusBar';

/** iOS `BravosPageColor` / `AuthPalette.pageBackground` — same shell as InviteLandingPage. */
const PAGE_BG = 'var(--bg-page)';

export const NotFound = () => {
  usePageStatusBar({ background: PAGE_BG });
  useHideAppNavbar();
  const navigate = useNavigate();

  // A direct hit on a bad URL has nothing to go back to, so don't offer a dead control.
  const [canGoBack] = useState(
    () => typeof window !== 'undefined' && window.history.length > 1
  );

  return (
    <div
      className="relative min-h-screen font-bravos antialiased text-theme-text"
      style={{ backgroundColor: PAGE_BG }}
    >
      <div className="relative z-10 flex min-h-screen flex-col">
        <header
          className="relative z-50 w-full shrink-0"
          style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 sm:py-4">
            <Link to="/" className="shrink-0" aria-label="Bravos home">
              <BravosWordmark className="h-9 w-auto sm:h-11" />
            </Link>
          </div>
        </header>

        <main className="flex flex-1 flex-col px-6 pb-10 pt-8 sm:px-10 lg:px-12">
          <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center text-center">
            <p className="font-pageTitle text-[5.5rem] font-extrabold leading-[0.9] tracking-[-0.05em] text-theme-text sm:text-[6.5rem]">
              404
            </p>
            <h1 className="mt-3.5 font-pageTitle text-[1.75rem] font-bold leading-[1.12] tracking-[-0.02em] text-theme-text">
              We can’t find that page
            </h1>
            <p className="mt-2.5 text-sm leading-relaxed text-[var(--text-secondary)] sm:text-base">
              The link may be broken, or the page may have moved.
            </p>

            <Link
              to="/"
              className="mt-7 inline-flex h-[52px] w-full items-center justify-center rounded-xl bg-[var(--action-primary)] text-base font-semibold text-[var(--ink)] transition-colors hover:bg-[var(--action-primary-pressed)] active:bg-[var(--success)]"
            >
              Go to Home
            </Link>

            {canGoBack ? (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="mx-auto mt-5 inline-flex items-center gap-1.5 text-sm text-[var(--text-secondary)] underline underline-offset-4 transition hover:opacity-80"
              >
                <ChevronLeft className="h-4 w-4" />
                Go back
              </button>
            ) : null}
          </div>
        </main>

        <footer
          className="relative z-40 shrink-0"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto flex max-w-7xl items-end justify-between gap-4 px-4 pb-3 pt-2 sm:px-6 sm:pb-4 sm:pt-2">
            <nav
              className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-left text-xs font-normal text-theme-text sm:gap-x-5 sm:text-sm"
              aria-label="Legal"
            >
              <Link to="/terms" className="transition hover:opacity-80">
                T&amp;Cs
              </Link>
              <Link to="/privacy" className="transition hover:opacity-80">
                Privacy Policy
              </Link>
            </nav>
            <p className="pointer-events-none shrink-0 text-right text-xs font-normal text-theme-text sm:text-sm">
              © 2026 Bravos
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default NotFound;
